import { authoringRoute } from "../../authoring.runtime"
import { withProjectBoundary } from "../project.runtime"
import { ScheduleEditor } from "./schedule-editor"
import { registerTab } from "lib/@components/tabs"
import { AuthoringGuidance } from "./authoring-guidance"
import { AuthoringMacroGuidance } from "./authoring-macro-guidance"
import { AuthoringAuthorNotesTab } from "./authoring-author-notes-tab"
import { AboveRightBarTabs, AuditEntryBelow, AuditTitle, CompareTitleTop } from "slot-definitions"
import { FormatAuditData } from "event-definitions"
import { normaliseAuthoringAmendsAuditFrequency } from "authoring/routes/project/amends/lib/normalise-authoring-amends-audit-frequency"
import { AuthoringAmendsType } from "../../../lib/enum/authoring-amends-type.mjs"
import { AuthoringAmendsAuditContent } from "authoring/routes/project/amends/components/authoring-amends-audit-content"
import { replaceAllWith } from "lib/@components/slot/replace-all-with"
import { AuthoringAmendsAuditTitle } from "authoring/routes/project/amends/components/authoring-amends-audit-title"
import { AuthoringIntroValueAction } from "authoring/routes/project/amends/components/authoring-intro-value-action"
import { TaskComparisonEntryAction } from "./task-comparison-entry-action"
import { ReviewScheduleDraft } from "./review-schedule-draft"
import { ScheduleEditorFormTop } from "./schedule-editor-form"
import { LeadingSummaryAmendsEditor } from "./leading-summary-amends-editor"
import { AuthoringReviewLeadingSummary } from "./authoring-review-leading-summary"
import { AuthoringReviewAudit } from "authoring/routes/project/amends/components/authoring-review-audit"
import { LeadingSummaryAuthorNoteBoxAction } from "./leading-summary-author-note-box-action"
import { TaskOrderAuthorNoteBoxAction } from "./task-order-author-note-box-action"
import { TaskAuthorNoteBoxAction } from "./task-author-note-box-action"
import { AuthoringAuthorNoteBoxAction } from "./authoring-author-note-box"
import { AuthoringAuthorNotesEntryAction } from "./authoring-author-notes-entry"
import { AuthoringRecommendationsTab } from "./authoring-recommendations-tab"
import { AuthoringGuidanceTitle } from "./authoring-guidance-title"
import { AuthoringRecommendationsTitle } from "./authoring-recommendations-title"
import { AuthoringRecommendationsNudge } from "./authoring-recommendations-nudge"
import {
    shouldShowAuthoringDraftHistory,
    shouldShowAuthoringRecommendations,
} from "./lib/authoring-recommendations-visibility-presenters"
import { AuthoringDraftHistoryTab } from "./authoring-draft-history-tab"

const ScheduleEditorWithBoundary = withProjectBoundary(ScheduleEditor)
const ReviewScheduleDraftWithBoundary = withProjectBoundary(ReviewScheduleDraft)

authoringRoute.register(
    "projects/:projectId/schedule-editor/:scheduleId",
    <ScheduleEditorWithBoundary $lazy priority={50} />
)

authoringRoute.register(
    "projects/:projectId/schedule-editor/:scheduleId/preview",
    <ScheduleEditorWithBoundary $lazy preview={true} priority={50} />
)

authoringRoute.register(
    "projects/:projectId/:scheduleId/review",
    <ReviewScheduleDraftWithBoundary $lazy priority={50} />
)

registerTab({
    tab: "ScheduleRightBar",
    id: "AuthoringRecommendations",
    title: <AuthoringRecommendationsTitle />,
    predicate(context) {
        return shouldShowAuthoringRecommendationsTab(context)
    },
    content: <AuthoringRecommendationsTab $lazy priority={29} />,
})

registerTab({
    tab: "ScheduleRightBar",
    id: "AuthoringDraftHistory",
    title: "History",
    predicate(context) {
        return shouldShowAuthoringDraftHistoryTab(context)
    },
    content: <AuthoringDraftHistoryTab $lazy priority={29} />,
})

registerTab({
    tab: "ScheduleRightBar",
    id: "AuthoringGuidance",
    title: <AuthoringGuidanceTitle />,
    predicate(context) {
        return context.isAuthoringScheduleEditor
    },
    content: <AuthoringGuidance $lazy priority={30} />,
})

registerTab({
    tab: "ScheduleRightBar",
    id: "AuthoringMacroGuidance",
    title: "Components",
    predicate(context) {
        return context.isAuthoringScheduleEditor
    },
    content: <AuthoringMacroGuidance $lazy priority={31} />,
})

registerTab({
    tab: "ScheduleRightBar",
    id: "AuthoringAuthorNotes",
    title: "Author Notes",
    predicate(context) {
        return context.isAuthoringScheduleEditor
    },
    content: <AuthoringAuthorNotesTab $lazy priority={32} />,
})

AuditEntryBelow(AuthoringAmendsType.AUTHORING_AMENDS).plug(<AuthoringReviewAudit />)
AuditEntryBelow(AuthoringAmendsType.AUTHORING_AMENDS).plug(<AuthoringAmendsAuditContent priority={200} />)
AuditTitle(AuthoringAmendsType.AUTHORING_AMENDS).plug(replaceAllWith(<AuthoringAmendsAuditTitle />))
FormatAuditData(AuthoringAmendsType.AUTHORING_AMENDS).handle(normaliseAuthoringAmendsAuditFrequency)
AuthoringAuthorNotesEntryAction.plug(<AuthoringIntroValueAction />)
AuthoringAuthorNotesEntryAction.plug(<TaskComparisonEntryAction />)
AuthoringAuthorNoteBoxAction.plug(<LeadingSummaryAuthorNoteBoxAction />)
AuthoringAuthorNoteBoxAction.plug(<TaskOrderAuthorNoteBoxAction />)
AuthoringAuthorNoteBoxAction.plug(<TaskAuthorNoteBoxAction />)

ScheduleEditorFormTop.plug(<LeadingSummaryAmendsEditor $lazy />)

CompareTitleTop.plug(<AuthoringReviewLeadingSummary $lazy replace if={(context) => context.reviewReferenceId} />)

AboveRightBarTabs.plug(<AuthoringRecommendationsNudge if={shouldShowAuthoringRecommendationsTab} />)

function shouldShowAuthoringRecommendationsTab(context) {
    return shouldShowAuthoringRecommendations(withAuthoringRecommendationsFlag(context))
}

function shouldShowAuthoringDraftHistoryTab(context) {
    return shouldShowAuthoringDraftHistory(withAuthoringRecommendationsFlag(context))
}

function withAuthoringRecommendationsFlag(context) {
    return {
        ...context,
        recommendationsEnabled: true,
    }
}
